<template>
  <v-row no-gutters>
    <template>
      <v-col cols="12" class="pr-0">
        <div class="upCardAnalyticsGraphic px-4 py-3" v-if="!noInfoFound">
          <v-col cols="12">
            <div class="d-flex flex-column">
              <div class="d-flex justify-space-between">
                <span class="body-bold-medium neutral-20-dark--text">{{
                  $t('message.pages.analyticsV3.locations')
                }}</span>
                <span class="body-regular-smaller neutral-40-light--text">
                  {{
                    $t('message.pages.analyticsV3.pages.devices.updatedAgo', {
                      time: lastFetchInMinutes,
                    })
                  }}
                </span>
              </div>

              <span class="body-regular-smaller neutral-40-light--text">
                {{ $t('message.pages.analyticsV3.locationsSubtitle') }}
                <a
                  class="body-bold-smaller neutral-30-base--text span-link"
                  @click="handleGoToAnalyticsArticle"
                >
                  {{ $t('message.commom.learnMore') }}
                  <v-icon size="12" color="neutral-30-base"
                    >mdi-open-in-new</v-icon
                  >
                </a>
              </span>
            </div>
          </v-col>
          <div
            :class="{
              hasSecondLevel: !isFirstLevel,
              isThirdLevel: isThirdLevel,
            }"
            class="d-flex"
          >
            <!-- back -->
            <base-btn
              v-if="!isFirstLevel"
              :disabled="loading"
              class="header__title--back body-bold-small mr-2"
              type="tertiary"
              icon="mdi-arrow-left"
              color="neutral-light"
              @click="handleBack"
            />
            <!-- subtitle::first level -->
            <div
              v-if="!isFirstLevel"
              class="header__title--sub d-flex align-center body-bold-small"
            >
              <h4 class="text-truncate" @click="handleBack({ isSub: true })">
                {{ this.filters.country }}
              </h4>
            </div>

            <!-- subtitle::second level -->
            <div
              v-if="isThirdLevel"
              class="header__title--sub2 d-flex align-center body-bold-small"
            >
              <v-icon>mdi-chevron-right</v-icon>
              <h4 class="text-truncate">{{ this.filters.region }}</h4>
            </div>
          </div>

          <v-col cols="12" class="pa-0">
            <v-row class="mt-4" v-if="loading" no-gutters>
              <v-row class="charts-container mb-8" v-if="loading">
                <v-col cols="3">
                  <DonutSkeleton />
                </v-col>
                <v-col cols="9">
                  <div class="device-cards-container">
                    <v-row>
                      <v-col class="skeleton">
                        <CardItemSkeleton />
                      </v-col>
                      <v-col class="skeleton">
                        <CardItemSkeleton />
                      </v-col>
                      <v-col class="skeleton">
                        <CardItemSkeleton />
                      </v-col>
                    </v-row>
                  </div>
                </v-col>
              </v-row>
            </v-row>
            <v-card
              class="d-flex flex-row align-center justify-center"
              flat
              height="250px"
              width="100%"
              v-else-if="errors.fetchError"
            >
              <v-row
                class="flex-column"
                justify="center"
                align="center"
                no-gutters
              >
                <v-card flat height="30" class="mb-4">
                  <span class="footerText align-text d-block">
                    {{ errors.shownErrMsg }}
                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon size="18" v-bind="attrs" v-on="on">
                          mdi-help-circle
                        </v-icon>
                      </template>
                      <v-container
                        class="text-body-2 pa-0"
                        style="max-width: 230px"
                      >
                        {{ errors.tooltipErrMsg }}
                      </v-container>
                    </v-tooltip>
                  </span>
                </v-card>
                <v-btn color="primary" class="ml-3" @click="fetchData">{{
                  $t('message.pages.analytics.tryAgain')
                }}</v-btn>
              </v-row>
            </v-card>
            <v-row v-else no-gutters align="center" ref="chartsContainer">
              <v-col cols="12" class="mt-5">
                <v-row no-gutters align="center">
                  <v-col class="mt-3 mb-4 d-flex justify-center" cols="3">
                    <div flat class="d-flex align-center justify-center">
                      <Donut
                        ref="donutChartComponent"
                        :series="series"
                        :labels="
                          chartData.map((c) =>
                            isFirstLevel ? countryName(c) : c.name
                          )
                        "
                        :plays="chartData.map((c) => c.play)"
                        :colors="donutColors"
                      />
                    </div>
                  </v-col>
                  <v-col cols="9">
                    <v-row :key="langVersion" class="align-center mb-4">
                      <template>
                        <v-col
                          v-for="(locale, i) in chartData"
                          :key="i"
                          :cols="
                            chartData.length === 1
                              ? 12
                              : chartData.length === 2
                              ? 6
                              : 4
                          "
                        >
                          <div
                            class="locationCard ma-1 d-flex flex-column align-center"
                            :class="{ 'py-14': chartData.length <= 3 }"
                          >
                            <country-flag
                              v-if="isFirstLevel"
                              :country="locale.country"
                              class="mb-1 rounded-full"
                              size="normal"
                            />
                            <div>
                              <span class="body-bold-xlarge">
                                {{ locale.percentage }}%
                              </span>
                            </div>
                            <div class="d-flex justify-center mt-1">
                              <span class="d-flex">
                                <div
                                  class="circle mr-2"
                                  style="margin-top: 2px"
                                  :style="`background-color: ${locale.color};`"
                                ></div>
                                <span
                                  class="body-label-caption neutral-40-light--text"
                                >
                                  {{
                                    locale.isOthers
                                      ? $t('message.playerPage.others')
                                      : countryName(locale)
                                  }}
                                </span>
                              </span>
                            </div>
                            <div class="grid__name">
                              <span
                                class="body-regular-smaller neutral-40-light--text"
                              >
                                {{ Number(locale.play).toLocaleString() }} Plays
                              </span>
                            </div>
                          </div>
                        </v-col>
                      </template>
                    </v-row>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-col>
        </div>
        <div v-if="isCapturingPng" class="capture-clone">
          <div ref="chartsContainerForCapture" class="upCardAnalyticsGraphic px-4 py-3">
            <v-col cols="12">
              <div class="d-flex flex-column">
                <div class="d-flex justify-space-between">
                  <span class="body-bold-medium neutral-20-dark--text">
                    {{ $t('message.pages.analyticsV3.locations') }}
                  </span>
                </div>
                <span class="body-regular-smaller neutral-40-light--text">
                  {{ $t('message.pages.analyticsV3.locationsSubtitle') }}
                </span>
              </div>
            </v-col>

            <div
              :class="{ hasSecondLevel: !isFirstLevel, isThirdLevel: isThirdLevel }"
              class="d-flex"
            >
              <div
                v-if="!isFirstLevel"
                class="header__title--sub d-flex align-center body-bold-small"
              >
                <h4 class="text-truncate neutral-20-dark--text">
                  {{ this.filters.country }}
                </h4>
              </div>
              <div
                v-if="isThirdLevel"
                class="header__title--sub2 d-flex align-center body-bold-small"
              >
                <v-icon class="neutral-20-dark--text">mdi-chevron-right</v-icon>
                <h4 class="text-truncate neutral-20-dark--text">
                  {{ this.filters.region }}
                </h4>
              </div>
            </div>

            <v-row no-gutters align="center">
              <v-col cols="12" class="mt-5">
                <v-row no-gutters align="center">
                  <v-col class="mt-3 mb-4 d-flex justify-center" cols="3">
                    <div flat class="d-flex align-center justify-center">
                      <Donut
                        :series="series"
                        :labels="chartData.map((c) => isFirstLevel ? countryName(c) : c.name)"
                        :plays="chartData.map((c) => c.play)"
                        :colors="donutColors"
                      />
                    </div>
                  </v-col>
                  <v-col cols="9">
                    <v-row :key="langVersion" class="align-center mb-4">
                      <template>
                        <v-col
                          v-for="(locale, i) in chartData"
                          :key="i"
                          :cols="chartData.length === 1 ? 12 : chartData.length === 2 ? 6 : 4"
                        >
                          <div class="locationCard ma-1 d-flex flex-column align-center">
                            <country-flag
                              v-if="isFirstLevel"
                              :country="locale.country"
                              class="mb-1 rounded-full"
                              size="normal"
                            />
                            <div>
                              <span class="body-bold-xlarge neutral-20-dark--tex">{{ locale.percentage }}%</span>
                            </div>
                            <div class="d-flex justify-center mt-1">
                              <span class="d-flex">
                                <div
                                  class="circle mr-2"
                                  style="margin-top: 2px"
                                  :style="`background-color: ${locale.color};`"
                                ></div>
                                <span class="body-label-caption neutral-40-light--text">
                                  {{ locale.isOthers ? $t('message.playerPage.others') : countryName(locale) }}
                                </span>
                              </span>
                            </div>
                            <div class="grid__name">
                              <span class="body-regular-smaller neutral-40-light--text">
                                {{ Number(locale.play).toLocaleString() }} Plays
                              </span>
                            </div>
                          </div>
                        </v-col>
                      </template>
                    </v-row>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </div>
        </div>
        <div v-if="isCapturingPdf" class="capture-clone-pdf">
          <div ref="pdfCaptureContainer" class="upCardAnalyticsGraphic px-4 py-3">
            <v-row no-gutters align="center">
              <v-col cols="12" class="mt-5">
                <v-row no-gutters align="center">
                  <v-col class="mt-3 mb-4 d-flex justify-center" cols="3">
                    <div flat class="d-flex align-center justify-center">
                      <Donut
                        :series="series"
                        :labels="chartData.map((c) => isFirstLevel ? countryName(c) : c.name)"
                        :plays="chartData.map((c) => c.play)"
                        :colors="donutColors"
                      />
                    </div>
                  </v-col>
                  <v-col cols="9">
                    <v-row :key="langVersion" class="align-center mb-4">
                      <v-col
                        v-for="(locale, i) in chartData"
                        :key="i"
                        :cols="chartData.length === 1 ? 12 : chartData.length === 2 ? 6 : 4"
                      >
                        <div class="locationCard ma-1 d-flex flex-column align-center">
                          <country-flag
                            v-if="isFirstLevel"
                            :country="locale.country"
                            class="mb-1 rounded-full"
                            size="normal"
                          />
                          <div>
                            <span class="body-bold-xlarge neutral-20-dark--text">{{ locale.percentage }}%</span>
                          </div>
                          <div class="d-flex justify-center mt-1">
                            <span class="d-flex">
                              <div
                                class="circle mr-2"
                                style="margin-top: 2px"
                                :style="`background-color: ${locale.color};`"
                              ></div>
                              <span class="body-label-caption neutral-40-light--text">
                                {{ locale.isOthers ? $t('message.playerPage.others') : countryName(locale) }}
                              </span>
                            </span>
                          </div>
                          <div class="grid__name">
                            <span class="body-regular-smaller neutral-40-light--text">
                              {{ Number(locale.play).toLocaleString() }} Plays
                            </span>
                          </div>
                        </div>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </div>
        </div>
        <div class="upCardAnalyticsTable pa-6">
          <v-col cols="12" class="tableOrganicContent pa-0">
            <v-row no-gutters>
              <div class="scrollable-content" @scroll="handleScroll">
                <div class="tableContent">
                  <BaseTable
                    :items="
                      search.length > 0 ? searchResults : filteredTableData
                    "
                    :columns="visibleTableColumns"
                    :loading="loading"
                    :items-per-page="10"
                    :show-pagination="true"
                    sum-field="view"
                    :rowHover="true"
                    :showTotalLabel="false"
                    :showActionColumn="!isThirdLevel"
                    @row-click="handleSelectFilter"
                    @update-visible-columns="currentlyVisibleColumns = $event"
                    :no-pointer="isThirdLevel"
                  >
                    <template #nameCell="{ row }">
                      <span
                        class="text-truncate"
                        :title="isFirstLevel ? countryName(row) : row.name"
                      >
                        {{
                          isFirstLevel
                            ? countryName(row)
                            : row.name
                            ? row.name
                            : '-'
                        }}
                      </span>
                    </template>
                    <template #playCell="{ row }">
                      <span class="body-regular-small">
                        {{
                          Number(row.play).toLocaleString(currencyLocale, {
                            currency: currency,
                          })
                        }}
                      </span>
                    </template>
                    <template #unique_playCell="{ row }">
                      <span class="body-regular-small">
                        {{
                          Number(row.unique_play).toLocaleString(
                            currencyLocale,
                            {
                              currency: currency,
                            }
                          )
                        }}
                      </span>
                    </template>
                    <template #viewCell="{ row }">
                      <span class="body-regular-small">
                        {{
                          Number(row.view).toLocaleString(currencyLocale, {
                            currency: currency,
                          })
                        }}
                      </span>
                    </template>

                    <template #unique_viewCell="{ row }">
                      <span class="body-regular-small">
                        {{
                          Number(row.unique_view).toLocaleString(
                            currencyLocale,
                            {
                              currency: currency,
                            }
                          )
                        }}
                      </span>
                    </template>

                    <template #playrateCell="{ row }">
                      <span class="body-regular-small">
                        {{ ((row.play / row.view) * 100).toFixed(1) }}%
                      </span>
                    </template>
                    <template #actionCell="{ hovered }">
                      <BaseBtn
                        v-if="hovered && !isThirdLevel"
                        class="mr-2"
                        type="tertiary"
                        size="small"
                        icon
                        style="
                          padding: 0;
                          min-width: 25px;
                          width: 25px;
                          height: 25px;
                          margin-left: 5px;
                        "
                      >
                        <v-icon size="12"> mdi-arrow-right</v-icon>
                      </BaseBtn>
                    </template>
                  </BaseTable>
                </div>
              </div>
            </v-row>
          </v-col>
        </div>
      </v-col>
    </template>
  </v-row>
</template>

<script>
// external imports
import { format, subDays } from 'date-fns'
import { mapState, mapMutations } from 'vuex'
import Fuse from 'fuse.js'

// internal imports
import i18n from '@/translations/lang'
import CountryFlag from 'vue-country-flag'
import { getCountryName, getCountryCode } from '@/utils/countryNames'

// API
import newAnalyticsApi from '@/services/newAnalyticsApi'

// Components
import CardItemSkeleton from '@/views/AnalyticsV3/components/SkeletonLoaders/CardItem.vue'
import DonutSkeleton from '@/views/AnalyticsV3/components/SkeletonLoaders/Donut.vue'
import Donut from '@/views/AnalyticsV3/components/Charts/Donut.vue'
import BaseTable from '@/components/BaseComponents/BaseTable.vue'
import { exportToCsv } from '@/utils/csvExporter.js'
import { toPng } from 'html-to-image'
import { generatePdfBlob } from '@/utils/pdfExporter.js'
import currencyMixin from '../../mixins/currencyMixin'
import { getEmbeddedFontCss } from '@/utils/fontBase64.js'

export default {
  name: 'Locales',
  components: {
    CardItemSkeleton,
    DonutSkeleton,
    Donut,
    BaseTable,
    CountryFlag,
    BaseBtn: () => import('@/components/BaseBtn.vue'),
  },
  mixins: [currencyMixin],
  props: {
    componentData: {
      type: Object,
      default: () => ({ selectedVideos: [], dateRange: [] }),
      required: true,
    },
  },
  data() {
    return {
      isCapturingPng: false, 
      isCapturingPdf: false,
      loading: false,
      lastFetchDate: new Date(),
      filters: {
        country: null,
        region: null,
      },
      filterLevel: 'country',
      columnName: {
        country: 'País',
        region: 'Região',
        city: 'Cidade',
      },
      currentlyVisibleColumns: [],
      allTableData: [],
      series: [],
      lastFetchInMinutes: 1,
      donutColors: [
        '#3F55EE',
        '#20C1A4',
        '#FF882B',
        '#9D1DA0',
        '#FFC107',
        '#CBCBCB',
      ],
      chartData: [],
      colors: [],
      noInfoFound: false,
      errors: {
        fetchError: false,
        shownErrMsg: '',
        tooltipErrMsg: '',
      },
      totalData: {
        views: 0,
        uniqueViews: 0,
        plays: 0,
        uniquePlays: 0,
      },
      page: 1,
      pageCount: 0,
      search: '',
      nothingFound: false,
      nameOrder: 'desc',
      viewsOrder: 'desc',
      playsOrder: 'desc',
      uniqueViewsOrder: 'desc',
      uniquePlaysOrder: 'desc',
      playrateOrder: 'desc',
      isScrolled: false,
      searchResults: [],
      langVersion: 0,
      analyticsLinks: {
        pt: 'https://help.pandavideo.com/pt-br/article/como-funcionam-as-metricas-do-analytics-na-panda-video-qk2m59/',
        en: 'https://help.pandavideo.com/en-us/article/how-do-analytics-metrics-work-in-panda-video-kinu6m/',
        es: 'https://help.pandavideo.com/es/article/como-funcionan-las-metricas-de-analytics-en-panda-video-cpkcfv/',
      },
    }
  },
  computed: {
    ...mapState({
      isMobileBreakpoint: (state) => state.user.isMobileBreakpoint,
      dateRange: (state) => state.analytics.filters.dateRange,
      selectedVideos: (state) =>
        state.analytics.filters.filterVideos.selectedVideos || [],
    }),
    currentLocale() {
      return this.$i18next.language
    },
    graphColors() {
      return this.chartData.map((c) => c.color)
    },
    apexOptions() {
      return {
        ...this.options,
        colors: this.graphColors,
        stroke: {
          ...(this.options.stroke || {}),
          colors: [this.pieBorderColor],
        },
      }
    },
    pieBorderColor() {
      return this.$vuetify.theme.dark ? '#171924' : '#FFFFFF'
    },
    isFirstLevel() {
      return this.filterLevel === 'country'
    },
    isSecondLevel() {
      return this.filterLevel === 'region'
    },
    isThirdLevel() {
      return this.filterLevel === 'city'
    },
    subtitle() {
      if (this.isSecondLevel) return this.filters.country
      return this.filters.region
    },
    tableHeight() {
      const length = this.allTableData.length
      if (length < 5) {
        return 267
      } else {
        return length * 48 + 5
      }
    },
    filteredTableData() {
      return this.allTableData.map((item) => ({
        ...item,
        playrate: ((item.play / item.view) * 100).toFixed(1),
      }))
    },
    baseTableColumns() {
      return [
        {
          text: this.isFirstLevel
            ? this.$t('message.pages.analytics.country')
            : this.isSecondLevel
            ? this.$t('message.pages.analytics.region')
            : this.$t('message.pages.analytics.city'),
          value: 'name',
          align: 'start',
          width: '40%',
        },
        {
          text: this.$t('message.pages.analytics.account.plays'),
          tooltip: this.$t('message.pages.analytics.account.playsTooltip'),
          value: 'play',
          align: 'end',
          width: '12.5%',
        },
        {
          text: this.$t('message.pages.analytics.account.uniquePlays'),
          tooltip: this.$t(
            'message.pages.analytics.account.uniquePlaysTooltip'
          ),
          value: 'unique_play',
          align: 'end',
          width: '12.5%',
        },

        {
          text: this.$t('message.pages.analytics.account.views'),
          tooltip: this.$t('message.pages.analytics.account.viewsTooltip'),
          value: 'view',
          align: 'end',
          width: '12.5%',
        },
        {
          text: this.$t('message.pages.analytics.account.uniqueViews'),
          tooltip: this.$t(
            'message.pages.analytics.account.uniqueViewsTooltip'
          ),
          value: 'unique_view',
          align: 'end',
          width: '12.5%',
        },
        {
          text: 'Play rate',
          tooltip: this.$t('message.pages.analytics.account.playrateTooltip'),
          value: 'playrate',
          align: 'end',
          width: '12.5%',
          formatter: (value, row) => {
            if (!row.view) {
              return '0.0%'
            }
            const playrate = ((row.play / row.view) * 100).toFixed(1)
            return `${playrate}%`
          },
        },
      ]
    },
    visibleTableColumns() {
      return this.baseTableColumns.map((col) => ({
        ...col,
        width: 100 / this.baseTableColumns.length + '%',
      }))
    },
  },
  watch: {
    async filterLevel() {
      await this.fetchData()
    },
    componentData: {
      handler() {
        this.fetchData()
      },
      deep: true,
      immediate: true,
    },
    selectedVideos: {
      handler() {
        this.fetchData()
      },
      deep: true,
    },
    currentLocale() {
      this.langVersion++
    },
    dateRange() {
      this.fetchData()
    },
    'dateRange.start'() {
      this.fetchData()
    },
    'dateRange.end'() {
      this.fetchData()
    },
  },
  methods: {
    ...mapMutations('analytics', ['UPDATE_ERROR_STATE']),
    handleGoToAnalyticsArticle() {
      const articleLink =
        this.analyticsLinks[this.$i18next.resolvedLanguage] ??
        this.analyticsLinks['en']
      window.open(articleLink, '_blank')
    },
    async fetchData() {
      this.noInfoFound = false
      this.allTableData = []
      this.chartData = []
      this.series = []
      this.colors = ['#4A148C', '#10859E', '#00695C', '#9E9D24', '#EF6C00']
      this.loading = true
      await this.getLocaleInfo()
      this.loading = false
      this.lastFetchInMinutes = 1
      this.lastFetchDate = new Date()
    },
    async getLocaleInfo() {
      if (this.selectedVideos && this.selectedVideos.length > 0) {
        await this.getLocaleInfoFromSelectedVideos()
        return
      }
      try {
        const start_date = this.dateRange?.start
          ? format(new Date(this.dateRange.start), 'yyyy-MM-dd')
          : format(subDays(new Date(), 7), 'yyyy-MM-dd')
        const end_date = this.dateRange?.end
          ? format(new Date(this.dateRange.end), 'yyyy-MM-dd')
          : format(new Date(), 'yyyy-MM-dd')
        const fetchDataFromFilter = {
          country: async () => {
            const { data } = await newAnalyticsApi.getCountryMetrics(
              start_date,
              end_date
            )
            const finalData = Object.keys(data).reduce((acc, key) => {
              const summedData = Object.values(data[key]).reduce(
                (acc, item) => {
                  acc.view += item.view
                  acc.unique_view += item.unique_view
                  acc.play += item.play
                  acc.unique_play += item.unique_play
                  return acc
                },
                {
                  view: 0,
                  unique_view: 0,
                  play: 0,
                  unique_play: 0,
                }
              )
              acc.push({
                name: key,
                ...summedData,
              })
              return acc
            }, [])
            return finalData
          },
          region: async () => {
            const ret = await newAnalyticsApi.getRegionMetrics(
              start_date,
              end_date,
              getCountryCode(this.filters.country)
            )
            const regionsData = Object.keys(
              ret.data[getCountryCode(this.filters.country)]
            ).reduce((acc, key) => {
              acc.push({
                name: key,
                ...ret.data[getCountryCode(this.filters.country)][key],
              })
              return acc
            }, [])
            return regionsData
          },
          city: async () => {
            const ret = await newAnalyticsApi.getCityMetrics(
              start_date,
              end_date,
              getCountryCode(this.filters.country),
              this.filters.region
            )
            const citiesData = Object.keys(
              ret.data[getCountryCode(this.filters.country)][
                this.filters.region
              ]
            ).reduce((acc, key) => {
              acc.push({
                name: key,
                ...ret.data[getCountryCode(this.filters.country)][
                  this.filters.region
                ][key].total,
                ...ret.data[getCountryCode(this.filters.country)][
                  this.filters.region
                ][key],
              })
              return acc
            }, [])
            return citiesData
          },
        }
        const data = await fetchDataFromFilter[this.filterLevel]()
        this.filterApiData(data)
        if (!this.chartData.length) this.noInfoFound = true
      } catch (e) {
        this.UPDATE_ERROR_STATE(true)
        this.handleFetchError(e)
      }
    },
    async getLocaleInfoFromSelectedVideos() {
      const summedData = []
      const searchAndSumVideo = async (videoId) => {
        const start_date = this.dateRange?.start
          ? format(new Date(this.dateRange.start), 'yyyy-MM-dd')
          : format(subDays(new Date(), 7), 'yyyy-MM-dd')
        const end_date = this.dateRange?.end
          ? format(new Date(this.dateRange.end), 'yyyy-MM-dd')
          : format(new Date(), 'yyyy-MM-dd')
        const fetchDataFromFilter = {
          country: async () => {
            const { data } = await newAnalyticsApi.getCountryMetricsFromVideo(
              videoId,
              start_date,
              end_date
            )
            const finalData = Object.keys(data).reduce((acc, key) => {
              const summedData = Object.values(data[key]).reduce(
                (acc, item) => {
                  acc.view += item.view
                  acc.unique_view += item.unique_view
                  acc.play += item.play
                  acc.unique_play += item.unique_play
                  return acc
                },
                {
                  view: 0,
                  unique_view: 0,
                  play: 0,
                  unique_play: 0,
                }
              )
              acc.push({
                name: key,
                ...summedData,
              })
              return acc
            }, [])
            return finalData
          },
          region: async () => {
            const ret = await newAnalyticsApi.getRegionMetricsFromVideo(
              videoId,
              start_date,
              end_date,
              getCountryCode(this.filters.country)
            )
            const regionsData = Object.keys(
              ret.data[getCountryCode(this.filters.country)]
            ).reduce((acc, key) => {
              acc.push({
                name: key,
                ...ret.data[getCountryCode(this.filters.country)][key],
              })
              return acc
            }, [])
            return regionsData
          },
          city: async () => {
            const ret = await newAnalyticsApi.getCityMetricsFromVideo(
              videoId,
              start_date,
              end_date,
              getCountryCode(this.filters.country),
              this.filters.region
            )
            const citiesData = Object.keys(
              ret.data[getCountryCode(this.filters.country)][
                this.filters.region
              ]
            ).reduce((acc, key) => {
              acc.push({
                name: key,
                ...ret.data[getCountryCode(this.filters.country)][
                  this.filters.region
                ][key].total,
                ...ret.data[getCountryCode(this.filters.country)][
                  this.filters.region
                ][key],
              })
              return acc
            }, [])
            return citiesData
          },
        }
        const data = await fetchDataFromFilter[this.filterLevel]()
        for (const item of data) {
          const exists = summedData.find((i) => i.name === item.name)
          if (exists) {
            exists.view += item.view
            exists.unique_view += item.unique_view
            exists.play += item.play
            exists.unique_play += item.unique_play
          } else {
            summedData.push(item)
          }
        }
      }
      let videosToSearch = this.selectedVideos.map((item) => item.videoId)
      const makeRequestWorker = async () => {
        while (videosToSearch.length) {
          const video = videosToSearch.pop()
          if (!video) return
          await searchAndSumVideo(video)
        }
      }
      const MAX_CONCURRENT_REQUESTS = 5
      const promises = []
      for (let i = 0; i < MAX_CONCURRENT_REQUESTS; i++) {
        promises.push(makeRequestWorker())
      }
      await Promise.all(promises)
      this.filterApiData(summedData)
    },
    filterApiData(data) {
      data = data.filter(
        (item) =>
          !(
            typeof item.name === 'string' &&
            item.name.trim().toLowerCase() === 'other'
          )
      )
      const totalViews = data.reduce((acc, item) => {
        acc += item.view
        return acc
      }, 0)

      const totalPlays = data.reduce((acc, item) => {
        acc += item.play
        return acc
      }, 0)

      const totalUniqueViews = data.reduce((acc, item) => {
        acc += item.unique_view
        return acc
      }, 0)

      const totalUniquePlays = data.reduce((acc, item) => {
        acc += item.unique_play
        return acc
      }, 0)

      this.totalData = {
        views: totalViews,
        uniqueViews: totalUniqueViews,
        plays: totalPlays,
        uniquePlays: totalUniquePlays,
      }

      function makeChangesInPlace(data, transformFn) {
        data.forEach((item) => {
          item.country = item.name.toLocaleLowerCase()
          item.name = transformFn(item.name)
        })
        return data
      }

      const formatedData = makeChangesInPlace(data, getCountryName)

      this.allTableData = formatedData.sort((a, b) => b.view - a.view)

      this.chartData = []

      let percentages = []
      let sortedData = Object.entries(this.allTableData)
        .map((item) => {
          return {
            name: item[0],
            ...item[1],
          }
        })
        .sort((a, b) => b.play - a.play)
      let othersObject = {
        name: 'others',
        country: 'others',
        isOthers: true,
        play: 0,
        unique_play: 0,
        unique_view: 0,
        view: 0,
        color: this.donutColors[5],
        percentage: 0,
      }
      for (let i = 0; i < sortedData.length; i++) {
        const maxShown = 5
        if (i < maxShown) {
          let percentage = Number(
            ((100 * sortedData[i].play) / totalPlays).toFixed(2)
          )
          percentages.push(percentage)
          let color = this.donutColors[i] ?? this.donutColors[5]
          this.chartData.push({
            color: color,
            percentage: percentage,
            ...sortedData[i],
          })
        } else {
          othersObject.play += sortedData[i].play ?? 0
          othersObject.unique_play += sortedData[i].unique_play ?? 0
          othersObject.unique_view += sortedData[i].unique_view ?? 0
          othersObject.view += sortedData[i].view ?? 0
        }
      }
      if (othersObject.play > 0) {
        othersObject.percentage = Number(
          ((100 * othersObject.play) / totalPlays).toFixed(2)
        )
        percentages.push(othersObject.percentage)
        this.chartData.push(othersObject)
      }
      this.series = percentages
    },
    handleSelectFilter(rowData) {
      if (this.loading || this.isThirdLevel) return

      let level = 'city'

      this.filters[this.filterLevel] = rowData.name

      if (this.isFirstLevel) level = 'region'

      this.filterLevel = level
    },
    handleBack({ isSub = false } = {}) {
      if (this.loading || (isSub && !this.isThirdLevel)) return

      let level = 'region'

      if (this.isSecondLevel) level = 'country'

      this.filterLevel = level
    },
    handleTitle() {
      if (this.loading || this.isFirstLevel) return

      this.filters.country = null
      this.filters.region = null
      this.filterLevel = 'country'
    },
    handleFetchError(err) {
      this.errors.fetchError = true
      this.errors.tooltipErrMsg =
        err.msg ??
        err?.response?.data?.errMsg ??
        err?.response?.data?.message ??
        'Error'
      this.errors.shownErrMsg = i18n.t('message.errors.unexpectedError')
    },
    clearErrors() {
      Object.keys(this.errors).forEach((e) => {
        if (typeof this.errors[e] === 'string') {
          return (this.errors[e] = '')
        }
        this.errors[e] = false
      })
    },
    doItemsSearch() {
      let searchValue = this.search
      if (searchValue && searchValue.length > 0) {
        let list = JSON.parse(JSON.stringify(this.allTableData))
        let fuseOptions = {
          keys: ['name'],
          includeScore: true,
          threshold: 0.4,
        }
        const fuse = new Fuse(list, fuseOptions)
        let searchResult = fuse.search(searchValue)
        const itemsResults = searchResult.map((result) => result.item)
        this.searchResults = itemsResults
      } else {
        this.searchResults = []
      }
    },
    getOrderByColumn(column) {
      switch (column) {
        case 'name':
          return this.nameOrder
        case 'views':
          return this.viewsOrder
        case 'plays':
          return this.playsOrder
        case 'uniqueViews':
          return this.uniqueViewsOrder
        case 'uniquePlays':
          return this.uniquePlaysOrder
        case 'playrate':
          return this.playrateOrder
        default:
          return null
      }
    },
    handleOrderByColumn(column) {
      switch (column) {
        case 'name':
          this.handleOrderByName()
          break
        case 'views':
          this.handleOrderByViews()
          break
        case 'plays':
          this.handleOrderByPlays()
          break
        case 'uniqueViews':
          this.handleOrderByUniqueViews()
          break
        case 'uniquePlays':
          this.handleOrderByUniquePlays()
          break
        case 'playrate':
          this.handleOrderByPlayrate()
          break
      }
    },
    handleOrderByName() {
      if (!this.nameOrder || this.nameOrder === 'desc') {
        this.nameOrder = 'asc'
      } else {
        this.nameOrder = 'desc'
      }
      if (this.nameOrder === 'asc') {
        this.allTableData.sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { numeric: true })
        )
      }
      if (this.nameOrder === 'desc') {
        this.allTableData.sort(
          (a, b) => -a.name.localeCompare(b.name, undefined, { numeric: true })
        )
      }
    },
    handleOrderByViews() {
      if (!this.viewsOrder || this.viewsOrder === 'desc') {
        this.viewsOrder = 'asc'
      } else {
        this.viewsOrder = 'desc'
      }
      if (this.viewsOrder === 'asc') {
        this.allTableData.sort((a, b) => a.view - b.view)
      }
      if (this.viewsOrder === 'desc') {
        this.allTableData.sort((a, b) => b.view - a.view)
      }
      if (this.search) this.doItemsSearch()
    },
    handleOrderByPlays() {
      if (!this.playsOrder || this.playsOrder === 'desc') {
        this.playsOrder = 'asc'
      } else {
        this.playsOrder = 'desc'
      }
      if (this.playsOrder === 'asc') {
        this.allTableData.sort((a, b) => a.play - b.play)
      }
      if (this.playsOrder === 'desc') {
        this.allTableData.sort((a, b) => b.play - a.play)
      }
      if (this.search) this.doItemsSearch()
    },
    handleOrderByUniqueViews() {
      if (!this.uniqueViewsOrder || this.uniqueViewsOrder === 'desc') {
        this.uniqueViewsOrder = 'asc'
      } else {
        this.uniqueViewsOrder = 'desc'
      }
      if (this.uniqueViewsOrder === 'asc') {
        this.allTableData.sort((a, b) => a.unique_view - b.unique_view)
      }
      if (this.uniqueViewsOrder === 'desc') {
        this.allTableData.sort((a, b) => b.unique_view - a.unique_view)
      }
      if (this.search) this.doItemsSearch()
    },
    handleOrderByUniquePlays() {
      if (!this.uniquePlaysOrder || this.uniquePlaysOrder === 'desc') {
        this.uniquePlaysOrder = 'asc'
      } else {
        this.uniquePlaysOrder = 'desc'
      }
      if (this.uniquePlaysOrder === 'asc') {
        this.allTableData.sort((a, b) => a.unique_play - b.unique_play)
      }
      if (this.uniquePlaysOrder === 'desc') {
        this.allTableData.sort((a, b) => b.unique_play - a.unique_play)
      }
      if (this.search) this.doItemsSearch()
    },
    handleOrderByPlayrate() {
      if (!this.playrateOrder || this.playrateOrder === 'desc') {
        this.playrateOrder = 'asc'
      } else {
        this.playrateOrder = 'desc'
      }
      if (this.playrateOrder === 'asc') {
        this.allTableData.sort((a, b) => a.play / a.view - b.play / b.view)
      }
      if (this.playrateOrder === 'desc') {
        this.allTableData.sort((a, b) => b.play / b.view - a.play / a.view)
      }
      if (this.search) this.doItemsSearch()
    },
    formatNumberTotalTraffic(number) {
      return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    },
    handleScroll(event) {
      const scrollTop = event.target.scrollTop
      this.isScrolled = scrollTop > 5
    },
    countryName(locale) {
      if (this.isFirstLevel) {
        if (
          locale.isOthers ||
          (typeof locale.name === 'string' &&
            locale.name.toLowerCase() === 'others')
        ) {
          return this.$t('message.playerPage.others')
        }
        const lang = i18n.language || 'pt'
        return getCountryName(locale.name, lang)
      }
      return locale.name
    },
    generateAndDownloadCsv() {
      const startDateFormatted = format(this.dateRange.start, 'dd_MM_yyyy')
      const endDateFormatted = format(this.dateRange.end, 'dd_MM_yyyy')
      const tableData =
        this.search.length > 0 ? this.searchResults : this.allTableData
      exportToCsv(
        `analytics-locations-${startDateFormatted}--${endDateFormatted}.csv`,
        this.currentlyVisibleColumns,
        tableData
      )
    },
    async generateAndDownloadPng() {
      this.isCapturingPng = true
      await this.$nextTick()
      await new Promise(resolve => setTimeout(resolve, 1000))
      const chartsElement = this.$refs.chartsContainerForCapture
      if (!chartsElement) {
        console.error('Elemento dos gráficos não encontrado para exportação.')
        return
      }
      try {
        const lines = chartsElement.querySelectorAll('line')
        for (const line of lines) {
          line.remove()
        }
        const textTruncateFixCss = `
          * {
            text-overflow: initial !important;
            overflow: visible !important;
            white-space: nowrap !important; 
          }
          .charts-container .d-flex {
            white-space: nowrap !important;
          }
        `
        const fontCss = await getEmbeddedFontCss()
        const imgDataUri = await toPng(chartsElement, {
          pixelRatio: 3,
          quality: 1,
          cacheBust: true,
          skipFonts: false,
          preferredFontFormat: 'woff2',
          fontEmbedCSS: fontCss + textTruncateFixCss,
          style: { fontFamily: 'Rubik, sans-serif' },
          fetchRequestInit: {
            mode: 'cors',
            credentials: 'omit',
          },
        })
        const startDateFormatted = format(this.dateRange.start, 'dd_MM_yyyy')
        const endDateFormatted = format(this.dateRange.end, 'dd_MM_yyyy')
        const link = document.createElement('a')
        link.download = `locations-${startDateFormatted}--${endDateFormatted}.png`
        link.href = imgDataUri
        link.click()
      } catch (error) {
        console.error('Erro ao gerar PNG:', error)
        this.UPDATE_ERROR_STATE(true)
      } finally {
        this.isCapturingPng = true
      }
    },
    handleLastFetchInMinutes() {
      const now = new Date()
      const diff = now.getTime() - this.lastFetchDate.getTime()
      const minutes = Math.floor(diff / (1000 * 60))
      this.lastFetchInMinutes = `${minutes + 1}`
    },
    async generatePdf() {
      this.isCapturingPdf = true
      await this.$nextTick()
      await new Promise(resolve => setTimeout(resolve, 1000))
      const chartsElement = this.$refs.pdfCaptureContainer

      if (!chartsElement) {
        console.error('Elemento dos gráficos não encontrado para exportação.')
        return
      }

      try {
        const lines = chartsElement.querySelectorAll('line')
        for (const line of lines) {
          line.remove()
        }
        const fontCss = await getEmbeddedFontCss()
        const imgDataUri = await toPng(chartsElement, {
          backgroundColor: '#FFFFFF',
          pixelRatio: 3,
          quality: 1,
          cacheBust: true,
          fontEmbedCSS: fontCss
        })

        const img = new Image()
        img.src = imgDataUri
        await new Promise((resolve) => {
          img.onload = resolve
        })

        const pdfImageWidth = 186
        const pdfImageHeight = (img.height * pdfImageWidth) / img.width

        const headerData = {
          title: this.$t('message.pages.analyticsV3.locations'),
          description: this.$t('message.pages.analyticsV3.locationsSubtitle'),
          metrics: [],
        }

        const tableHeaders = this.baseTableColumns.map((col) => col.text)

        const tableData =
          this.search.length > 0 ? this.searchResults : this.allTableData

        const sortedDataForPdf = [...tableData].sort((a, b) => {
          const playsA = a.play || 0
          const playsB = b.play || 0
          return playsB - playsA
        })

        const tableBody = sortedDataForPdf.map((item) =>
          this.baseTableColumns.map((col) => {
            let cellValue = item[col.value]

            if (col.formatter) {
              return col.formatter(cellValue, item)
            }

            if (col.value === 'name') {
              return this.isFirstLevel
                ? this.countryName(item)
                : item.name || '-'
            }

            if (typeof cellValue === 'number') {
              return cellValue.toLocaleString()
            }

            return cellValue || '-'
          })
        )

        const columnStyles = {
          0: { cellWidth: 'auto', overflow: 'ellipsize' },
          1: { cellWidth: 22 },
          2: { cellWidth: 22 },
          3: { cellWidth: 22 },
          4: { cellWidth: 22 },
          5: { cellWidth: 22 },
        }

        const startDateFormatted = format(this.dateRange.start, 'dd_MM_yyyy')
        const endDateFormatted = format(this.dateRange.end, 'dd_MM_yyyy')
        const filename = `locations-${startDateFormatted}--${endDateFormatted}.pdf`

        const pdfBlob = await generatePdfBlob({
          chartHeaderData: headerData,
          htmlImageData: {
            uri: imgDataUri,
            width: pdfImageWidth,
            height: pdfImageHeight,
          },
          tableHeaders: tableHeaders,
          tableBody: tableBody,
          columnStyles: columnStyles,
        })
        this.$root.$emit('pdf-ready', { blob: pdfBlob, filename: filename })
      } catch (error) {
        console.error('Erro ao gerar PDF:', error)
        this.UPDATE_ERROR_STATE(true)
      } finally {
        this.isCapturingPng = false
      }
    },
  },
  mounted() {
    this.$root.$on('trigger-csv-export', this.generateAndDownloadCsv)
    this.$root.$on('trigger-png-export', this.generateAndDownloadPng)
    this.$root.$on('trigger-pdf-export', this.generatePdf)
    setInterval(() => {
      this.handleLastFetchInMinutes()
    }, 60500)
  },
  beforeDestroy() {
    this.$root.$off('trigger-csv-export', this.generateAndDownloadCsv)
    this.$root.$off('trigger-png-export', this.generateAndDownloadPng)
    this.$root.$off('trigger-pdf-export', this.generatePdf)
  },
}
</script>
<style lang="scss" scoped>
.device-cards-container {
  width: 100%;
}
.charts-container {
  margin: 24px -12px 0 -12px;

  .col {
    display: flex;
    justify-content: center;
    align-items: center;
    padding-top: 0;
    padding-bottom: 0;
  }
}
.locationCard {
  background-color: varFigma(neutral-99);
  padding: 24px;
  border-radius: 8px;

  .circle {
    width: 12px;
    height: 12px;
    border-radius: 50px;
  }
}

.grid {
  &__flag {
    grid-area: flag;

    > span {
      transform-origin: top;
      margin: {
        top: 0.4em;
        left: -0.8em;
      }
    }
  }
}

.v-card {
  &.isThirdLevel {
    cursor: initial;

    &:focus::before {
      opacity: 0;
    }

    &::v-deep {
      .v-ripple__container {
        display: none;
      }
    }
  }
}

.upCardAnalyticsGraphic,
.upCardAnalyticsTable {
  border-radius: 8px;
  background-color: varFigma(neutral-100-white);

  .title {
    color: varFigma(neutral-20-dark);
  }
  .subtitle {
    color: varFigma(neutral-30-base);
  }
}

.upCardAnalyticsTable {
  margin-top: 1rem;
}
.tableOrganicContent {
  padding: 0 1rem;
}

.paginationTable {
  .v-pagination__navigation {
    box-shadow: none;
  }
  .v-pagination__navigation {
    border: 1px solid varFigma(neutral-95-lightest);
    border-radius: 12px;

    &:hover {
      border: 1px solid varFigma(brand-80-light);
    }
  }
  .v-pagination__item {
    border-radius: 12px;
    box-shadow: none;
    border: 1px solid varFigma(neutral-95-lightest);
    &:hover {
      border: 1px solid varFigma(brand-80-light);
    }
  }
  .v-pagination__item.primary {
    box-shadow: none;
    border: 1px solid varFigma(brand-60-base);
    background: varFigma(brand-95-lighter) !important;
    color: varFigma(brand-40-dark) !important;
    &:hover {
      border: 1px solid varFigma(brand-80-light) !important;
      background: varFigma(brand-80-light) !important;
      color: varFigma(brand-30-darker) !important;
    }
  }
}

.v-data-table {
  .v-data-table__wrapper > table > thead > tr > th {
    background: varFigma(neutral-100-white);
    color: varFigma(neutral-20-dark);
    font-family: Rubik;
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 100%;
    padding: 16px 0;
    border-bottom: 1px solid varFigma(neutral-95-lightest);
    text-align: inherit;
    min-width: 125px;
  }

  .v-data-table__wrapper
    > table
    > tbody
    > tr:hover:not(.v-data-table__expanded__content):not(.v-data-table__empty-wrapper) {
    background-color: varFigma(neutral-100-white) !important;
  }
  .v-data-table__wrapper
    > table
    > tbody
    > tr:not(:last-child)
    > td:not(.v-data-table__mobile-row) {
    border-bottom: thin solid varFigma(neutral-95-lightest) !important;
    min-width: 125px;
  }
  .v-data-footer__pagination {
    margin-left: auto;
    padding: 1rem 0;
  }
  .v-data-footer__select {
    display: none;
  }
  .v-data-footer {
    display: none;
  }
}

.table-header {
  position: sticky;
  top: 120px;
  z-index: 1;
  background: varFigma(neutral-100-white);
  padding: 16px 0;
  border-bottom: 1px solid varFigma(neutral-95-lightest);
  transition: all 0.3s ease;

  &.scrolled {
    box-shadow: 0 8px 16px -4px rgba(0, 0, 0, 0.15);
  }
}

.tableHeaderTitles {
  width: 100%;
  background: varFigma(neutral-100-white);

  &--item {
    color: varFigma(neutral-40-light);
    text-align: inherit;
    min-width: 125px;
  }
}

.tableViews {
  width: 97%;
  margin-top: 8px;
  flex: 1;
  display: flex;
  flex-direction: column;

  .v-data-table__wrapper {
    flex: 1;
    overflow-y: visible;

    #SvgjsPath1370 {
      stroke: red;
    }
  }
}

.content-footer {
  width: 100%;
  margin-top: auto;
  padding: 16px 0;
  background: varFigma(neutral-100-white);
}

.recordsNumber {
  color: varFigma(neutral-40-light);
}

.emptyStateTrafficModal {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;

  .emptyState {
    &--title {
      color: varFigma(neutral-20-dark);
    }
    &--subtitle {
      color: varFigma(neutral-40-light);
    }
  }

  @media (max-height: 1024px) {
    padding: 32dvh 0;
  }
  @media (max-height: 768px) {
    padding: 30dvh 0;
  }
  .body-label-xs {
    color: varFigma(neutral-20-dark);
    font-family: Rubik;
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 100%;
  }
}

.scrollable-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
  width: 99%;
}

.breadcrumb-flex {
  display: flex;
  align-items: center;
  font-size: 18px;
  margin-bottom: 16px;
}
.breadcrumb-item {
  cursor: pointer;
  font-weight: 500;
  color: var(--v-brand-darken2);
  transition: color 0.2s;
}
.breadcrumb-item:hover {
  color: var(--v-blueMarine-base);
}

.capture-clone {
  position: absolute;
  top: -9999px;
  left: -9999px;
  pointer-events: none;
  width: 1200px;
  height: 500px;
  background: white;
  padding: 24px;
  box-sizing: border-box;
}

.capture-clone-pdf {
  position: absolute;
  top: -4999px;
  left: -4999px;
  pointer-events: none;
  width: 900px;
  height: 500px;
  box-sizing: border-box;
}
</style>
